import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon } from 'lucide-react';
import { InboundEmailConfig, EmailRule } from './EmailConfiguration';
interface EmailRulesProps {
  inboundConfigs: InboundEmailConfig[];
  setInboundConfigs: React.Dispatch<React.SetStateAction<InboundEmailConfig[]>>;
  locale: string;
}
export function EmailRules({
  inboundConfigs,
  setInboundConfigs,
  locale
}: EmailRulesProps) {
  const [searchTerm, setSearchTerm] = useState('');
  // Filter rules across all configs
  const allRules = inboundConfigs.flatMap(config => config.rules.map(rule => ({
    ...rule,
    configEmailAddress: config.emailAddress,
    configScope: config.scopeLevel,
    configEntityName: config.entityName,
    configSiteName: config.siteName
  }))).filter(rule => rule.configEmailAddress.toLowerCase().includes(searchTerm.toLowerCase()) || rule.senderMatch.toLowerCase().includes(searchTerm.toLowerCase()) || rule.subjectContains.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="space-y-6" data-id="element-16026">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-16027">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-16028">
          <div className="flex-1 max-w-md" data-id="element-16029">
            <div className="relative rounded-md shadow-sm" data-id="element-16030">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-16031">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-16032" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search rules..." data-id="element-16033" />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16034">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-16035">
          <thead className="bg-gray-50" data-id="element-16036">
            <tr data-id="element-16037">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16038">
                Email Config
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16039">
                Sender Match
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16040">
                Subject Contains
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16041">
                Actions
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16042">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-16043">
            {allRules.map(rule => <tr key={rule.id} data-id="element-16044">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-16045">
                  {rule.configEmailAddress}
                  <br data-id="element-16046" />
                  <span className="text-xs text-gray-500" data-id="element-16047">
                    {rule.configScope === 'Tenant' ? 'Tenant-wide' : rule.configScope === 'Entity' ? `Entity: ${rule.configEntityName}` : `Site: ${rule.configSiteName}`}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16048">
                  {rule.senderMatch}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16049">
                  {rule.subjectContains}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16050">
                  {rule.reclassifyAs && <div data-id="element-16051">Reclassify as: {rule.reclassifyAs}</div>}
                  {rule.autoForwardTo && <div data-id="element-16052">Forward to: {rule.autoForwardTo}</div>}
                  {rule.applyTags.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-16053">
                      {rule.applyTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-16054">
                          {tag}
                        </span>)}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-16055">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-16056">
                    {rule.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}